import { forwardRef, useEffect, useRef, useState, SVGProps } from "react";
import {
  useCreate,
  useGetIdentity,
  useNavigation,
  useShow,
  useParsed,
  useIsAuthenticated,
} from "@refinedev/core";

import { LeftOutlined } from "@ant-design/icons";
import {
  Button,
  Typography,
  Modal,
  Stack,
  CircularProgress,
  Box,
} from "@mui/material";

import AppButton from "components/AppButton";
import { DisplayCanvas } from "components/canvas";
import { ColorSelect } from "components/color-select";
import { AvatarPanel } from "components/avatar";
import { colors } from "utility";
import { Canvas, Shape } from "types";
import { LogList } from "components/logs";
import { createShape, drawShapes } from "utility/whiteboard";

type Colors = (typeof colors)[number];
type ShapeType = "polyline" | "rect" | "line" | "circle" | "elliscpe";

interface Setting {
  shapeId?: string;
  type: ShapeType;
  color: Colors;
  width: number;
}

export const CanvasShow: React.FC = () => {
  const { pathname } = useParsed();
  const [open, setOpen] = useState<boolean>(false);
  const { data: identity } = useGetIdentity<any>();
  const { data: { authenticated } = {} } = useIsAuthenticated();

  const {
    queryResult: { data: { data: canvas } = {} },
  } = useShow<Canvas>();
  const { mutate } = useCreate();
  const { list, push } = useNavigation();

  const [setting, setSetting] = useState<Setting>({
    type: "polyline",
    width: 2,
    color: "black",
  });

  const saveShape = (shape) => {
    if (!authenticated) {
      if (pathname) {
        return push(`/login?to=${encodeURIComponent(pathname)}`);
      }

      return push(`/login`);
    }

    if (canvas?.id) {
      mutate({
        resource: "shapes",
        values: {
          data: shape,
          canvas_id: canvas?.id,
          user_id: identity.id,
        },
        meta: {
          canvas,
        },
        successNotification: false,
      });
    }
  };

  return (
    <div className="container">
      <div className="paper">
        <div className="paper-header">
          <Button
            onClick={() => list("canvases")}
            style={{ textTransform: "uppercase" }}
          >
            <LeftOutlined />
            Back
          </Button>
          <Typography variant="h3" fontSize="2rem">
            {canvas?.name ?? canvas?.id ?? ""}
          </Typography>
          <AppButton
            onClick={() => setOpen(true)}
            variant="contained"
            style={{
              background: "#3ecf8e",
              textTransform: "capitalize",
              fontSize: "14px",
              padding: "4px 16px",
            }}
          >
            View Changes
          </AppButton>
        </div>
        <Modal
          open={open}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            width: "90%",
            maxWidth: "550px",
            borderRadius: "8px",
            minHeight: "550px",
            overflowY: "auto",
          }}
        >
          <Stack
            style={{ background: "white", padding: "24px", height: "100%" }}
          >
            <Typography variant="h3">Canvas Changes</Typography>
            <LogList currentCanvas={canvas} />
            <AppButton
              onClick={() => setOpen(false)}
              key="close"
              variant="contained"
              style={{ marginTop: "auto" }}
            >
              Close
            </AppButton>
          </Stack>
        </Modal>

        {canvas ? (
          <DisplayCanvas canvas={canvas}>
            {(shapes) =>
              shapes ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 48,
                  }}
                >
                  <div>
                    <ColorSelect
                      selected={setting.color}
                      onChange={(color) => setSetting((v) => ({ ...v, color }))}
                    />
                  </div>

                  <Whiteboard
                    canvas={canvas}
                    shapes={shapes}
                    setting={setting}
                    onComplete={saveShape}
                  />
                  <div style={{ alignSelf: "flex-start" }}>
                    <AvatarPanel shapes={shapes} />
                  </div>
                </div>
              ) : (
                <div className="spin-wrapper">
                  <CircularProgress color="primary" />
                </div>
              )
            }
          </DisplayCanvas>
        ) : (
          <div className="spin-wrapper">
            <CircularProgress color="primary" />
          </div>
        )}
      </div>
    </div>
  );
};

interface CanvasItemProps extends SVGProps<SVGSVGElement> {
  canvas: Canvas;
  shapes: Shape[] | undefined;
  setting: Setting;
  onComplete: (shape: any) => void;
}

export const Whiteboard = ({
  canvas: { id, name, width, height },
  shapes,
  setting,
  onComplete,
  ...rest
}: CanvasItemProps) => {
  const ref = useRef<SVGSVGElement>(null);
  const [data, setData] = useState<Array<any>>([]);
  const [drawing, setDrawing] = useState(false);
  const curElement = useRef<SVGElement>();
  const [scale, setScale] = useState(null);

  const resize = () => {
    if (!ref.current) return;

    const newScale = {
      x: ref.current.clientWidth / Number(width),
      y: ref.current.clientHeight / Number(height),
    };

    while (ref.current.lastElementChild) {
      ref.current.removeChild(ref.current.lastElementChild);
    }
    drawShapes(shapes, ref.current, newScale);
  };
  useEffect(() => {
    window.addEventListener("resize", resize);

    return () => {
      window.addEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    if (!curElement.current || !data.length) return;

    curElement.current.setAttribute(
      "points",
      data.map((point) => `${point.x},${point.y}`).join(" ")
    );
  }, [data]);

  useEffect(() => {
    if (!ref?.current) return;

    const newScale = {
      x: ref.current.clientWidth / Number(width),
      y: ref.current.clientHeight / Number(height),
    };

    while (ref.current.lastElementChild) {
      ref.current.removeChild(ref.current.lastElementChild);
    }
    drawShapes(shapes, ref.current, newScale);
  }, [shapes]);

  const handleMouseDown = (event) => {
    setDrawing(true);

    curElement.current = createShape(setting);
    ref.current.appendChild(curElement.current);

    setData([
      ...data,
      {
        x: event.nativeEvent.offsetX,
        y: event.nativeEvent.offsetY,
      },
    ]);
  };

  const handleMouseMove = (event) => {
    if (!drawing) return;

    setData([
      ...data,
      {
        x: event.nativeEvent.offsetX,
        y: event.nativeEvent.offsetY,
      },
    ]);
  };

  const handleMouseUp = () => {
    setDrawing(false);
    const shape = {
      ...setting,
      value: { points: data },
    };

    onComplete(shape);

    setData([]);
  };

  return (
    <Box
      boxShadow="rgba(0, 0, 0, 0.1) 0px 4px 12px"
      marginBottom={2}
      width={width ?? "600px"}
      height={height ?? "400px"}
    >
      <svg
        ref={ref}
        style={{
          width: "100%",
          height: "100%",
          border: "1px solid black",
          userSelect: "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        {...rest}
      ></svg>
    </Box>
  );
};
